import { useState, useEffect } from 'react'
import { Download, X, Smartphone, Share, Plus } from 'lucide-react'
import { usePWA } from '../../hooks/usePWA'

const DISMISSED_KEY = 'aqua_install_dismissed'

/** Detecta si el dispositivo es iOS (iPhone / iPad / iPod) */
function isIOS() {
  const ua = navigator.userAgent
  // iPad moderno reporta como Mac con touch — detección dual
  const isIPadOS = ua.includes('Mac') && 'ontouchend' in document
  return /iPhone|iPad|iPod/i.test(ua) || isIPadOS
}

/** ¿La app ya se está ejecutando instalada? */
function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as any).standalone === true
}

export default function InstallBanner() {
  const { canInstall, isInstalled, installPWA } = usePWA()
  const [dismissed, setDismissed] = useState(false)
  const [visible, setVisible] = useState(false)
  const [iosMode, setIosMode] = useState(false)
  const [showIosHelp, setShowIosHelp] = useState(false)

  useEffect(() => {
    const alreadyDismissed = localStorage.getItem(DISMISSED_KEY)
    if (alreadyDismissed) { setDismissed(true); return }

    if (isStandalone()) return

    // iOS: mostrar banner con instrucciones manuales
    if (isIOS()) {
      setIosMode(true)
      const t = setTimeout(() => setVisible(true), 3000)
      return () => clearTimeout(t)
    }

    // Android/Desktop: esperar evento beforeinstallprompt
    if (canInstall && !isInstalled) {
      const t = setTimeout(() => setVisible(true), 3000)
      return () => clearTimeout(t)
    }
  }, [canInstall, isInstalled])

  const handleInstall = async () => {
    if (iosMode) {
      setShowIosHelp(true)
      return
    }
    const accepted = await installPWA()
    if (accepted) setVisible(false)
  }

  const handleDismiss = () => {
    setVisible(false)
    setShowIosHelp(false)
    setDismissed(true)
    localStorage.setItem(DISMISSED_KEY, '1')
  }

  if (!visible || dismissed || isInstalled) return null

  return (
    <>
      <div className="fixed bottom-20 left-4 right-4 z-50 lg:left-auto lg:right-6 lg:bottom-6 lg:w-80 animate-slide-up">
        <div className="bg-white rounded-2xl shadow-xl border border-aqua-100 p-4 flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-aqua-500 flex items-center justify-center flex-shrink-0">
            <Smartphone size={20} className="text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-800 leading-tight">
              Instala Aqua en tu celular
            </p>
            <p className="text-xs text-gray-500 mt-0.5 leading-snug">
              Accede más rápido, sin abrir el navegador. Funciona sin conexión.
            </p>

            <div className="flex gap-2 mt-3">
              <button
                onClick={handleInstall}
                className="flex-1 flex items-center justify-center gap-1.5 bg-aqua-500 hover:bg-aqua-600 text-white text-xs font-bold py-2 px-3 rounded-xl transition-colors"
              >
                {iosMode ? <><Share size={13} /> Cómo instalar</> : <><Download size={13} /> Instalar</>}
              </button>
              <button
                onClick={handleDismiss}
                className="flex items-center justify-center gap-1.5 text-gray-400 hover:text-gray-600 text-xs font-semibold py-2 px-3 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors"
              >
                Ahora no
              </button>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="text-gray-300 hover:text-gray-500 transition-colors mt-0.5 flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Instrucciones iOS */}
      {showIosHelp && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowIosHelp(false)} />
          <div className="relative bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl shadow-2xl">
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1.5 bg-gray-200 rounded-full" />
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Instalar en iPhone/iPad</h2>
                <button onClick={() => setShowIosHelp(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                  <X size={18} />
                </button>
              </div>

              <p className="text-xs text-gray-500 mb-4">
                Safari no permite instalar directamente. Sigue estos 3 pasos:
              </p>

              <div className="space-y-3">
                {/* Paso 1 */}
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-2xl">
                  <div className="w-7 h-7 rounded-full bg-aqua-500 text-white font-bold text-xs flex items-center justify-center flex-shrink-0">1</div>
                  <div className="flex-1 text-sm text-gray-700">
                    Abre esta página en <span className="font-bold">Safari</span> (no en Chrome ni Instagram)
                  </div>
                </div>

                {/* Paso 2 */}
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-2xl">
                  <div className="w-7 h-7 rounded-full bg-aqua-500 text-white font-bold text-xs flex items-center justify-center flex-shrink-0">2</div>
                  <div className="flex-1 text-sm text-gray-700">
                    Toca el ícono <span className="inline-flex items-center justify-center w-6 h-6 mx-0.5 rounded-md bg-blue-500 text-white align-middle"><Share size={12} /></span> <span className="font-bold">Compartir</span> (abajo en iPhone, arriba en iPad)
                  </div>
                </div>

                {/* Paso 3 */}
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-2xl">
                  <div className="w-7 h-7 rounded-full bg-aqua-500 text-white font-bold text-xs flex items-center justify-center flex-shrink-0">3</div>
                  <div className="flex-1 text-sm text-gray-700">
                    Elige <span className="font-bold">«Añadir a pantalla de inicio»</span> <span className="inline-flex items-center justify-center w-6 h-6 mx-0.5 rounded-md bg-gray-200 text-gray-700 align-middle"><Plus size={12} /></span> y toca <span className="font-bold">Añadir</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleDismiss}
                className="w-full mt-5 py-3 bg-aqua-500 text-white rounded-xl font-semibold text-sm hover:bg-aqua-600"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
